import React, { createRef } from "react";
import ReactDOM from "react-dom";
import {
  BrickWrapper,
  event,
  EventEmitter,
  method,
  property,
  UpdatingElement,
} from "@next-core/brick-kit";
import {
  DebuggerStore,
  FunctionDataToSave,
  FunctionDebuggerStore,
} from "./FunctionDebuggerStore";
import {
  DebuggerActionInitFunction,
  DebuggerStateActiveTab,
  DebuggerStateDebugInput,
  DebuggerStateDebugOutput,
  DebuggerStateOriginalFunction,
  DebuggerStateTestCase,
  DebuggerStateTestExpect,
  DebuggerStateTestInput,
} from "./reducers/interfaces";
import { ProcessedCoverage, TestStats } from "../shared/functions/interfaces";

export interface FunctionDebuggerStoreProps {
  runTestsAutomaticallyTimeout?: number;
  breakpoints?: number[];
  onActivetabChange?: (event: CustomEvent) => void;
  onOriginalfunctionChange?: (event: CustomEvent) => void;
  onFunctionModified?: (event: CustomEvent) => void;
  onDebuginputChange?: (event: CustomEvent) => void;
  onDebugoutputChange?: (event: CustomEvent) => void;
  onTestsChange?: (event: CustomEvent) => void;
  onTestinputChange?: (event: CustomEvent) => void;
  onTestexpectChange?: (event: CustomEvent) => void;
  onTestreceivedChange?: (event: CustomEvent) => void;
  onTestmatchedChange?: (event: CustomEvent) => void;
  onTestupdatableChange?: (event: CustomEvent) => void;
  onSomethingModified?: (event: CustomEvent) => void;
  onTeststatsChange?: (event: CustomEvent) => void;
  onCoverageChange?: (event: CustomEvent) => void;
  onDebuggerinfoChange?: (event: CustomEvent) => void;
}

export interface FunctionDebuggerStoreElementProps {
  runTestsAutomaticallyTimeout?: number;
  breakpoints?: number[];
  onActivetabChange?: (event: CustomEvent<DebuggerStateActiveTab>) => void;
  onOriginalfunctionChange?: (event: CustomEvent<DebuggerStateOriginalFunction>) => void;
  onFunctionModified?: (event: CustomEvent<boolean>) => void;
  onDebuginputChange?: (event: CustomEvent<DebuggerStateDebugInput>) => void;
  onDebugoutputChange?: (event: CustomEvent<DebuggerStateDebugOutput>) => void;
  onTestsChange?: (event: CustomEvent<DebuggerStateTestCase[]>) => void;
  onTestinputChange?: (event: CustomEvent<DebuggerStateTestInput>) => void;
  onTestexpectChange?: (event: CustomEvent<DebuggerStateTestExpect>) => void;
  onTestreceivedChange?: (event: CustomEvent<DebuggerStateDebugOutput>) => void;
  onTestmatchedChange?: (event: CustomEvent<boolean | null>) => void;
  onTestupdatableChange?: (event: CustomEvent<boolean>) => void;
  onSomethingModified?: (event: CustomEvent<boolean>) => void;
  onTeststatsChange?: (event: CustomEvent<TestStats>) => void;
  onCoverageChange?: (event: CustomEvent<ProcessedCoverage>) => void;
  onDebuggerinfoChange?: (event: CustomEvent<unknown>) => void;
}

/**
 * @id next-builder.function-debugger-store
 * @author Steve
 * @history
 * 1.x.0: 新增构件 `next-builder.function-debugger-store`
 * @docKind brick
 * @noInheritDoc
 */
export class FunctionDebuggerStoreElement extends UpdatingElement implements FunctionDebuggerStoreProps {
  /**
   * @kind number
   * @description Run tests automatically in milliseconds.
   */
  @property({ type: Number })
  runTestsAutomaticallyTimeout: number;

  @property({ attribute: false })
  breakpoints: number[];

  @method()
  updateSource(source: string): void {
    this._storeRef.current.dispatch({
      type: "updateSource",
      source,
    });
  }

  @method()
  updateTypescript(typescript: boolean): void {
    this._storeRef.current.dispatch({
      type: "updateTypescript",
      typescript,
    });
  }

  @method()
  initFunction(data: Omit<DebuggerActionInitFunction, "type">): void {
    this._storeRef.current.initFunction(data);
  }

  @method()
  switchTab(tab: string): void {
    this._storeRef.current.dispatch({
      type: "switchTab",
      tab,
    });
  }

  @method()
  updateDebugInput(input: string): void {
    this._storeRef.current.dispatch({
      type: "updateDebugInput",
      input,
    });
  }

  @method()
  run(): void {
    this._storeRef.current.run();
  }

  @method()
  debuggerStart(): void {
    this._storeRef.current.debuggerStart();
  }

  @method()
  debuggerContinue(): void {
    this._storeRef.current.debuggerContinue();
  }

  @method()
  debuggerStep(): void {
    this._storeRef.current.debuggerStep();
  }

  @method()
  debuggerDisconnect(): void {
    this._storeRef.current.debuggerDisconnect();
  }

  @method()
  saveDebugAsTest(): void {
    this._storeRef.current.saveDebugAsTest();
  }

  @method()
  addTest(): void {
    this._storeRef.current.addTest();
  }

  @method()
  deleteTest(): void {
    this._storeRef.current.deleteTest();
  }

  @method()
  saveTest(): void {
    this._storeRef.current.saveTest();
  }

  @method()
  updateTestInput(input: string): void {
    this._storeRef.current.updateTestInput(input);
  }

  @method()
  runAllTests(): void {
    this._storeRef.current.runAllTests();
  }

  @method()
  getFunctionDataToSave(): Promise<FunctionDataToSave> {
    const store = this._storeRef.current;
    store.runAllTests();
    return Promise.resolve().then(() =>
      this._storeRef.current.getFunctionDataToSave()
    );
  }

  @event({ type: "activeTab.change" })
  private _activeTabChangeEmitter: EventEmitter<DebuggerStateActiveTab>;

  @event({ type: "originalFunction.change" })
  private _originalFunctionChangeEmitter: EventEmitter<DebuggerStateOriginalFunction>;

  @event({ type: "function.modified" })
  private _functionModifiedEmitter: EventEmitter<boolean>;

  @event({ type: "debugInput.change" })
  private _debugInputChangeEmitter: EventEmitter<DebuggerStateDebugInput>;

  @event({ type: "debugOutput.change" })
  private _debugOutputChangeEmitter: EventEmitter<DebuggerStateDebugOutput>;

  @event({ type: "tests.change" })
  private _testsChangeEmitter: EventEmitter<DebuggerStateTestCase[]>;

  @event({ type: "testInput.change" })
  private _testInputChangeEmitter: EventEmitter<DebuggerStateTestInput>;

  @event({ type: "testExpect.change" })
  private _testExpectChangeEmitter: EventEmitter<DebuggerStateTestExpect>;

  @event({ type: "testReceived.change" })
  private _testReceivedChangeEmitter: EventEmitter<DebuggerStateDebugOutput>;

  @event({ type: "testMatched.change" })
  private _testMatchedChangeEmitter: EventEmitter<boolean | null>;

  @event({ type: "testUpdatable.change" })
  private _testUpdatableChangeEmitter: EventEmitter<boolean>;

  @event({ type: "something.modified" })
  private _somethingModifiedEmitter: EventEmitter<boolean>;

  @event({ type: "testStats.change" })
  private _testStatsChangeEmitter: EventEmitter<TestStats>;

  @event({ type: "coverage.change" })
  private _coverageChangeEmitter: EventEmitter<ProcessedCoverage>;

  @event({ type: "debuggerInfo.change" })
  private _debuggerInfoChangeEmitter: EventEmitter<unknown>;

  private _handleActiveTabChange = (
    activeTab: DebuggerStateActiveTab
  ): void => {
    this._activeTabChangeEmitter.emit(activeTab);
  };
  private _handleOriginalFunctionChange = (
    originalFunction: DebuggerStateOriginalFunction
  ): void => {
    this._originalFunctionChangeEmitter.emit(originalFunction);
  };

  private _handleFunctionModified = (modified: boolean): void => {
    this._functionModifiedEmitter.emit(modified);
  };

  private _handleDebugInputChange = (input: DebuggerStateDebugInput): void => {
    this._debugInputChangeEmitter.emit(input);
  };

  private _handleDebugOutputChange = (
    output: DebuggerStateDebugOutput
  ): void => {
    this._debugOutputChangeEmitter.emit(output);
  };

  private _handleTestsChange = (tests: DebuggerStateTestCase[]): void => {
    this._testsChangeEmitter.emit(tests);
  };

  private _handleTestInputChange = (
    testInput: DebuggerStateTestInput
  ): void => {
    this._testInputChangeEmitter.emit(testInput);
  };

  private _handleTestExpectChange = (
    testExpect: DebuggerStateTestExpect
  ): void => {
    this._testExpectChangeEmitter.emit(testExpect);
  };

  private _handleTestReceivedChange = (
    output: DebuggerStateDebugOutput
  ): void => {
    this._testReceivedChangeEmitter.emit(output);
  };

  private _handleTestMatchedChange = (matched: boolean | null): void => {
    this._testMatchedChangeEmitter.emit(matched);
  };

  private _handleTestUpdatableChange = (updatable: boolean): void => {
    this._testUpdatableChangeEmitter.emit(updatable);
  };

  private _handleSomethingModified = (modified: boolean): void => {
    this._somethingModifiedEmitter.emit(modified);
  };

  private _handleCoverageChange = (coverage: ProcessedCoverage): void => {
    this._coverageChangeEmitter.emit(coverage);
  };

  private _handleDebuggerInfoChange = (info: unknown): void => {
    this._debuggerInfoChangeEmitter.emit(info);
  };

  private _handleTestStatsChange = (stats: TestStats): void => {
    this._testStatsChangeEmitter.emit(stats);
  };

  private _storeRef = createRef<DebuggerStore>();

  connectedCallback(): void {
    // Don't override user's style settings.
    // istanbul ignore else
    if (!this.style.display) {
      this.style.display = "block";
    }
    this._render();
  }

  disconnectedCallback(): void {
    ReactDOM.unmountComponentAtNode(this);
  }

  protected _render(): void {
    // istanbul ignore else
    if (this.isConnected) {
      ReactDOM.render(
        <BrickWrapper>
          <FunctionDebuggerStore
            ref={this._storeRef}
            runTestsAutomaticallyTimeout={this.runTestsAutomaticallyTimeout}
            breakpoints={this.breakpoints}
            onActiveTabChange={this._handleActiveTabChange}
            onOriginalFunctionChange={this._handleOriginalFunctionChange}
            onFunctionModified={this._handleFunctionModified}
            onDebugInputChange={this._handleDebugInputChange}
            onDebugOutputChange={this._handleDebugOutputChange}
            onTestsChange={this._handleTestsChange}
            onTestInputChange={this._handleTestInputChange}
            onTestExpectChange={this._handleTestExpectChange}
            onTestReceivedChange={this._handleTestReceivedChange}
            onTestMatchedChange={this._handleTestMatchedChange}
            onTestUpdatableChange={this._handleTestUpdatableChange}
            onSomethingModified={this._handleSomethingModified}
            onTestStatsChange={this._handleTestStatsChange}
            onCoverageChange={this._handleCoverageChange}
            onDebuggerInfoChange={this._handleDebuggerInfoChange}
          />
        </BrickWrapper>,
        this
      );
    }
  }
}

customElements.define(
  "next-builder.function-debugger-store",
  FunctionDebuggerStoreElement
);
